import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnDestroy, inject } from '@angular/core'
import {
  ElderBasicPaneLayoutComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderToastService,
} from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { StoragePath } from '../../storage-path'
import { PackageCheckinMode, StorageNode } from '../storage-node'
import { StorageNodeBrowserComponent } from '../storage-node-browser/storage-node-browser.component'
import { StorageNodeDetailComponent } from '../storage-node-detail/storage-node-detail.component'
import { StorageNodeService } from '../storage-node.service'

@Component({
  selector: 'app-storage-node-md',
  templateUrl: './storage-node-md.component.html',
  styleUrls: ['./storage-node-md.component.scss'],
  imports: [
    StorageNodeBrowserComponent,
    StorageNodeDetailComponent,
    AsyncPipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
  ],
})
export class StorageNodeMdComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _currentNode$: BehaviorSubject<StorageNode> = new BehaviorSubject(null)

  private readonly _destroy = new Subject<void>()

  private storageNodeService = inject(StorageNodeService)

  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    this._destroy.next()
    this._destroy.complete()
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get currentNode$(): Observable<StorageNode> {
    return this._currentNode$.asObservable()
  }

  @Input()
  public set currentNode(node: StorageNode) {
    this._currentNode$.next(node)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onPathChange(path: StoragePath) {
    // allows to select node upon view init
    if (path.isValidStorageNodePath) {
      this.storageNodeService.findOne(path.serialize()).subscribe({
        next: (node) => (this.currentNode = node),
        error: (err) => this.toastService.error('Failed to load node', err),
      })
    } else {
      this.currentNode = null
    }
  }

  public onNewNode(path: StoragePath): void {
    this._currentNode$.next(this.createNewNode(path))
  }

  public onDelete(event: Event): void {}

  public onClose(event: Event): void {}

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private createNewNode(path: StoragePath): StorageNode {
    const node = new StorageNode()
    node.ancestors = path.elements
    node.packageCheckinMode = PackageCheckinMode.AUTO

    return node
  }
}
